import { memo, useCallback, useContext, useMemo, useRef, Fragment, ReactElement } from 'react';
import {
  EditOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  HistoryOutlined,
  ProfileOutlined,
  PlusOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { Button, Flex, Space, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';

import { Table, TableComponent, TableProps } from '@ohpen/enso-ui';
import { ActionEntityType, FieldType, FirmGetAllResponse, KeyValue } from '@pmp/api';
import { Guard, useGuard } from '@ohpen/react-authz';

import { columns, firmExportColumns, firmSqeFields } from '../tableConfig';
import { firmExportRows } from '../useFirmsSqe';
import { FirmsListProps } from '../interfaces';
import { useListTableState } from '@pmp/hooks';
import { useExportTemplatesStore } from '@pmp/stores';
import { formatDate, lockActionTitle, withSqeFilterModes } from '@pmp/utils';
import { PmpConfigContext } from '@pmp/contexts';
import styles from '../styles.module.css';

const entityType = ActionEntityType.FIRMS;
const rowKey = 'ohpenId';
// Every firm-scoped BFF route takes `id`, so that is the key the assistant reports, not the table's.
const assistantRowKey = 'id';

export const FirmsList = memo(
  ({
    isFetching,
    data,
    total,
    setServerSideFiltering,
    exportFetchAll,
    onCreate,
    onView,
    onEdit,
    onDelete,
    onSeeFirmCases,
    onManageFirmCases,
    onSeeFirmIntermediaries,
    onViewChangeLog,
    valueLists,
  }: FirmsListProps<FirmGetAllResponse>): ReactElement => {
    const tableRef = useRef<TableComponent | null>(null);
    const { t } = useTranslation();
    const { allowed: showRowSelection } = useGuard({ flags: ['enableExports'] });
    const tableState = useListTableState<FirmGetAllResponse>(entityType, assistantRowKey, tableRef, columns);
    const { allowed: canManageCases } = useGuard({ permissions: ['brokerPortalCases:update'] });
    const { allowed: canViewCases } = useGuard({ permissions: ['brokerPortalCases:view'] });
    const { isFlexibleProcFeeActive } = useContext(PmpConfigContext);
    const { isLoadingExportTemplates, getExportTemplates } = useExportTemplatesStore();

    const getTemplates = useCallback(() => getExportTemplates(entityType), [getExportTemplates]);

    const exportSelectedRows = useCallback(async () => firmExportRows(tableRef.current?.getState().selection.rows ?? []), []);

    const onAdd = useCallback(() => {
      onCreate();
    }, [onCreate]);

    const getRowActions: (record: FirmGetAllResponse) => ReactElement[] = useCallback(
      record => {
        const rowActions = [
          <Tooltip key="view" title={t('enso.buttons.view')} placement="top">
            <Button type="text" aria-label="View firms" icon={<EyeOutlined />} onClick={() => onView(record)} />
          </Tooltip>,
          <Guard key="edit" permissions={['firms:update']} flags={['enableFirmsCrud']}>
            <Tooltip key="edit" title={lockActionTitle('edit', record.lockedByActionId)} placement="top">
              <Button
                type="text"
                aria-label="Edit firms"
                icon={<EditOutlined key="edit" />}
                onClick={() => onEdit(record)}
                disabled={!!record.lockedByActionId}
              />
            </Tooltip>
          </Guard>,
          // TODO PMP-1813 restore once the active-cases check for firms is implemented
          // <Guard key="delete" permissions={['firms:delete']} flags={['enableFirmsCrud']}>
          //   <Tooltip key="delete" title={lockActionTitle('delete', record.lockedByActionId)} placement="top">
          //     <Button
          //       type="text"
          //       aria-label="Delete firms"
          //       icon={<DeleteOutlined key="delete" rev={undefined} />}
          //       onClick={() => onDelete(record)}
          //       disabled={!!record.lockedByActionId}
          //     />
          //   </Tooltip>
          // </Guard>,
          canManageCases ? (
            <Tooltip key="cases" title={t('firms.labels.manageFirmCases')} placement="top">
              <Button
                type="text"
                aria-label="Manage Firm Cases"
                icon={<ProfileOutlined key="edit" />}
                onClick={() => onManageFirmCases(record)}
              />
            </Tooltip>
          ) : canViewCases ? (
            <Tooltip key="cases" title={t('firms.labels.seeFirmCases')} placement="top">
              <Button
                type="text"
                aria-label="See Firm Cases"
                icon={<ProfileOutlined key="edit" />}
                onClick={() => onSeeFirmCases(record)}
              />
            </Tooltip>
          ) : (
            <Fragment key="cases" />
          ),
          <Guard key="changeLog" flags={['enableChangeLog']}>
            <Tooltip title={t('lender.changeLog.labels.viewChangeLog')} placement="top">
              <Button type="text" aria-label="View change log" icon={<HistoryOutlined />} onClick={() => onViewChangeLog?.(record)} />
            </Tooltip>
          </Guard>,
          <Guard key="seeFirmIntermediaries" permissions={['brokers:view']} flags={['enableBrokers']}>
            <Tooltip key="seeFirmIntermediaries" title={t('firms.labels.seeFirmIntermediaries')} placement="top">
              <Button
                type="text"
                aria-label="See firms' intermediaries"
                icon={<TeamOutlined key="seeFirmIntermediaries" />}
                onClick={() => onSeeFirmIntermediaries(record)}
              />
            </Tooltip>
          </Guard>,
        ];
        return rowActions;
      },
      [
        t,
        onView,
        onEdit,
        onSeeFirmCases,
        onManageFirmCases,
        onSeeFirmIntermediaries,
        onViewChangeLog,
        onDelete,
        canManageCases,
        canViewCases,
      ],
    );

    const renderOperations = useCallback(
      (operations: KeyValue[]) => {
        const operationLabels = valueLists?.[FieldType.OPERATION_TYPE];
        const labels = operations.map(operation => operationLabels?.[operation.value ?? ''] ?? operation.value).join(', ');
        const keys = new Set(operations.map(operation => operation.key));
        if (!(isFlexibleProcFeeActive && keys.has('ADVICE') && keys.has('PACKAGE'))) return labels;

        return (
          <Space size="small">
            {labels}
            <Tooltip title={t('firms.labels.packagingWithAdviceFlexibleProcFeeWarning')} placement="top">
              <ExclamationCircleOutlined className={styles.operationsWarningIcon} />
            </Tooltip>
          </Space>
        );
      },
      [t, valueLists, isFlexibleProcFeeActive],
    );

    const tableColumns = useMemo(
      () =>
        withSqeFilterModes(columns, firmSqeFields).map(column =>
          column.key === 'operationsWhitelist' ? { ...column, render: renderOperations } : column,
        ),
      [renderOperations],
    );

    const tableProps: TableProps<ActionEntityType, FirmGetAllResponse> = useMemo(
      () => ({
        ...tableState,
        id: entityType,
        ref: tableRef,
        dataSource: data ?? [],
        columns: tableColumns,
        exportColumns: firmExportColumns,
        rowKey,
        pagination: { total, defaultPageSize: 20 },
        serverSideFiltering: true,
        setServerSideFiltering,
        scroll: { x: 'max-content', y: '100%' },
        resizableColumns: true,
        loading: isFetching,
        showRowSelection,
        getRowActions,
        showViews: true,
        valueLists,
      }),
      [tableState, getRowActions, valueLists, data, tableColumns, isFetching, showRowSelection, total, setServerSideFiltering],
    );

    return (
      <Table {...tableProps}>
        <Table.ActionsBar>
          <Flex justify="space-between">
            <Space size="middle">
              <Table.GlobalSearch />
            </Space>
            <Space size="middle">
              <Table.ResetFiltersButton />
              <Table.SelectColumnsButton />
              <Guard flags={['enableExports']}>
                <Table.ExportButton
                  editableFileName
                  fileName={`Firms ${formatDate()}`}
                  sheetName="Firms"
                  disabled={isFetching}
                  loadingTemplates={isLoadingExportTemplates}
                  getTemplates={getTemplates}
                  modalDefaults={{ columnScope: 'template' }}
                  exportView
                  exportFetchAll={exportFetchAll}
                />
              </Guard>
              <Guard permissions={['firms:create']} flags={['enableFirmsCrud']}>
                <Button icon={<PlusOutlined />} onClick={onAdd} type="primary">
                  {t('enso.labels.create')}
                </Button>
              </Guard>
            </Space>
          </Flex>
        </Table.ActionsBar>
        <Table.BulkBar xItemsSelectedLabel="firms.labels.xFirmsSelected">
          <Space size="middle">
            <Guard flags={['enableExports']}>
              <Table.ExportButton
                editableFileName
                fileName={`Firms ${formatDate()}`}
                sheetName="Firms"
                disabled={isFetching}
                loadingTemplates={isLoadingExportTemplates}
                getTemplates={getTemplates}
                modalDefaults={{ columnScope: 'template' }}
                exportView
                exportAll={false}
                exportFetchAll={exportSelectedRows}
              />
            </Guard>
          </Space>
        </Table.BulkBar>
      </Table>
    );
  },
);
